<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { useManufacturersStore } from "~/stores/manufacturers";
const { $api } = useNuxtApp();

// -----------------------
// composables
// -----------------------
const config = useRuntimeConfig();
const route = useRoute();
const manufacturersStore = useManufacturersStore();
const { t } = useI18n();

// -----------------------
// reactive properties
// -----------------------
const isCustomPage = computed(() => {
  if (!cmsPageError.value && !cmsPageResponse.value) {
    return null;
  }

  if (cmsPageError.value && cmsPageError.value.statusCode === 404) {
    return false;
  } else if (cmsPage.value) {
    return true;
  }
});
const cmsPage = computed(() => {
  if (cmsPageResponse.value) {
    return cmsPageResponse.value;
  }

  return null;
});
const manufacturerRef = computed(() => {
  return manufacturersStore.manufacturers.find(
    (m) => m.slug === route.params.slug.toLowerCase(),
  );
});
const manufacturerNameRef = computed(() => {
  if (manufacturerRef.value && "name" in manufacturerRef.value) {
    return manufacturerRef.value.name;
  }

  return "";
});

const filtersRef = computed(() => {
  if (manufacturerRef.value) {
    return `masterproduct_gyarto_property = '${manufacturerRef.value?.name}'`;
  }

  return "";
});

const breadcrumbRef = computed(() => {
  return [
    { url: `/${config.public.routes.manufacturers}`, label: t("our_brands") },
    { label: manufacturerNameRef.value },
  ];
});

// fixme: need to prevent the 404 error show up in console

const { data: cmsPageResponse, error: cmsPageError } = await useAsyncData(() =>
  $api("cmspage-from-url", {
    method: "POST",
    body: {
      url: `/${config.public.routes.manufacturerCmspage}/${route.params.slug}`,
    },
  }).catch((e) => {
    if (e.statusCode === 404) {
      throw e;
    } else {
      Sentry.captureEvent({
        message: `Failed to fetch CMS page content from API - ER-KV6Q`,
        level: "error",
        extra: {
          file: "components/Manufacturers/ManufacturersPage.vue",
          url: `/${config.public.routes.manufacturerCmspage}/${route.params.slug}`,
          error: e,
        },
      });

      return null;
    }
  }),
);

watch(
  () => isCustomPage.value,
  () => {
    if (isCustomPage.value === false && manufacturerRef.value) {
      useHead({
        title: manufacturerRef.value.meta_title,
        meta: [
          {
            name: "description",
            content: manufacturerRef.value.meta_description,
          },
        ],
      });
    }
  },
);
</script>

<template>
  <BreadcrumbBase :items="breadcrumbRef" />
  <template v-if="isCustomPage === true">
    <CmsPage :page-data="cmsPage" />
  </template>
  <template v-if="isCustomPage === false">
    <ProductList
      :title="manufacturerNameRef"
      :filters="filtersRef"
      :hide-facets="['masterproduct_gyarto_property']"
      :before-content="manufacturerRef?.content_before_productlist"
      :after-content="manufacturerRef?.content_after_productlist"
      :preserve-scroll-position="true"
    />
  </template>
</template>
